import React, { useContext, useEffect } from 'react';
import { getAllPages, getAllPosts, getMenu, getSettings } from './api/content-api';
import ContentContext from './store/content-context';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Page from './model/Page';
import ScrollToTop from './pages/ScrollToTop';
import ErrorPage from './pages/ErrorPage';
import RegisterForm from './components/Forms/Register/RegisterForm';
import LoginForm from './components/Forms/LoginForm';
import ResetPasswordForm from './components/Forms/ResetPasswordForm';
import { accountPage, loginPage, panelPage, passwordResetPage, registrationPage } from './store/fixed-routing';
import PanelPage from './pages/PanelPage';
import AccountPage from './pages/AccountPage';
import { Helmet } from 'react-helmet';
import AuthContext from './store/auth-context';

function App() {
	const { pages, setPages, setMenu, setSettings, registrationEnabled } = useContext(ContentContext);

	useEffect(() => {
		const fetchData = async () => {
			const pages = await getAllPages();
			const posts = await getAllPosts();
			const settings = await getSettings();
			const menu = await getMenu();
			setMenu(menu);
			setPages([...pages, ...posts]);
			setSettings(settings);
		};
		fetchData().catch((error) => console.log(error));
	}, []);

	const headData = {
		title: process.env.REACT_APP_TITLE,
		meta: [
			{ name: 'description', content: process.env.REACT_APP_DESCRIPTION },
			{ name: 'keywords', content: process.env.REACT_APP_KEYWORDS },
			{ name: 'robots', content: 'index, follow' },
			{ name: 'twitter:card', content: process.env.REACT_APP_OG_TWITTER_CARD },
			{ name: 'twitter:title', content: process.env.REACT_APP_OG_TITLE },
			{ name: 'twitter:description', content: process.env.REACT_APP_OG_DESCRIPTION },
			{ name: 'twitter:image', content: process.env.REACT_APP_OG_IMAGE },
			{ property: 'og:title', content: process.env.REACT_APP_OG_TITLE },
			{ property: 'og:description', content: process.env.REACT_APP_OG_DESCRIPTION },
			{ property: 'og:type', content: 'website' },
			{ property: 'og:url', content: process.env.REACT_APP_CANONICAL },
			{ property: 'og:image', content: process.env.REACT_APP_OG_IMAGE },
			{ property: 'og:image:width', content: process.env.REACT_APP_OG_IMAGE_W },
			{ property: 'og:image:height', content: process.env.REACT_APP_OG_IMAGE_H },
			{ property: 'og:site_name', content: process.env.REACT_APP_OG_TITLE },
			{ property: 'og:locale', content: 'pl_PL' },
		],
		link: [
			{ rel: 'canonical', href: process.env.REACT_APP_CANONICAL },
			{ rel: 'icon', href: process.env.REACT_APP_ICON },
			{ rel: 'manifest', href: process.env.REACT_APP_MANIFEST },
		],
		script: process.env.REACT_APP_SCRIPT,
	};

	return (
		<ScrollToTop>
			<Helmet>
				<title>{headData.title}</title>
				{headData.meta &&
					headData.meta.map((meta, index) =>
						meta.name ? (
							<meta
								key={index}
								name={meta.name}
								content={meta.content}
							/>
						) : (
							<meta
								key={index}
								property={meta.property}
								content={meta.content}
							/>
						),
					)}
				{headData.script && <script type='application/ld+json'>{headData.script}</script>}
				{headData.link &&
					headData.link.map((link, index) => (
						<link
							rel={link.rel}
							href={link.href}
						/>
					))}
			</Helmet>
			<Routes>
				{pages.map((pageData: Page) => pageData.toRoute())}
				<Route
					key={'default'}
					path={'*'}
					element={pages.length > 0 ? <ErrorPage /> : <></>}
				/>
				{registrationEnabled && (
					<>
						<Route
							path={registrationPage}
							element={pages.length > 0 ? <RegisterForm /> : <></>}
						/>
						<Route
							path={loginPage}
							element={pages.length > 0 && <LoginForm />}
						/>
						<Route
							path={passwordResetPage}
							element={pages.length > 0 && <ResetPasswordForm />}
						/>
						<Route
							path={accountPage}
							element={
								<PrivateRoute>
									<AccountPage />
								</PrivateRoute>
							}
						/>
						<Route
							path={panelPage + '/*'}
							element={
								<PrivateRoute>
									<PanelPage />
								</PrivateRoute>
							}
						/>
					</>
				)}
			</Routes>
		</ScrollToTop>
	);
}

const PrivateRoute = (props: { children: React.ReactNode }): JSX.Element => {
	const { children } = props;
	const { isLoggedIn } = useContext(AuthContext);
	const location = useLocation();

	return isLoggedIn() ? (
		<>{children}</>
	) : (
		<Navigate
			replace={true}
			to='/login'
			state={{ from: `${location.pathname}${location.search}` }}
		/>
	);
};

export default App;
